"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ComplianceStandards() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const [fillProgress, setFillProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const el = timelineRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const total = rect.height + viewportH * 0.5;
      const scrolled = viewportH - rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / total));
      setFillProgress(progress);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  const milestones = [{
    year: "2003",
    title: "Establishment of Quality Protocols",
    description: "Our journey began with the formalization of rigorous safety and quality standards, setting the foundation for our industry-leading compliance framework.",
    significant: true,
    image: ""
  }, {
    year: "2005",
    title: "First ISO Certification Achievement",
    description: "We successfully attained our initial ISO certification, validating our commitment to operational excellence and international quality benchmarks.",
    significant: false,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/compliance_iso_audit.png"
  }, {
    year: "2007",
    title: "Expansion into Global Regulatory Markets",
    description: "Our compliance team successfully navigated complex international regulatory landscapes, enabling our expansion into key global markets.",
    significant: true,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/compliance_global_expansion.png"
  }, {
    year: "2009",
    title: "Implementation of Advanced Audit Systems",
    description: "We integrated automated audit tracking software to ensure real-time visibility and accountability across all production lines.",
    significant: false,
    image: ""
  }, {
    year: "2011",
    title: "Industry Leadership in Safety Standards",
    description: "Recognized as a pioneer in safety protocols, we began setting the benchmarks that many industry peers now follow today.",
    significant: true,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/compliance_safety_award.png"
  }, {
    year: "2013",
    title: "Publication of Compliance Best Practices",
    description: "We shared our internal methodologies through a comprehensive guide, contributing to the broader advancement of industry safety standards.",
    significant: false,
    image: ""
  }, {
    year: "2015",
    title: "Inauguration of the Compliance Center",
    description: "A dedicated facility was opened to house our specialized testing equipment and regulatory documentation archives.",
    significant: true,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/compliance_center_opening.png"
  }, {
    year: "2017",
    title: "Adoption of Sustainable Compliance Metrics",
    description: "We incorporated environmental impact assessments into our core compliance reporting, aligning safety with sustainability.",
    significant: false,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/compliance_sustainability_report.png"
  }, {
    year: "2019",
    title: "Certification of Advanced Material Testing",
    description: "Our laboratory received accreditation for high-precision material testing, ensuring the integrity of every component we supply.",
    significant: true,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/compliance_material_testing.png"
  }, {
    year: "2021",
    title: "Digital Transformation of Regulatory Records",
    description: "We transitioned to a fully blockchain-secured digital ledger for all compliance documentation, ensuring immutable record-keeping.",
    significant: false,
    image: ""
  }, {
    year: "2023",
    title: "Two Decades of Unbroken Compliance",
    description: "Celebrating twenty years of zero-incident operations, a testament to our unwavering dedication to safety and quality.",
    significant: true,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/compliance_two_decades.png"
  }, {
    year: "2024",
    title: "Launch of the Future-Ready Standards Initiative",
    description: "We introduced a proactive framework to anticipate and adapt to emerging global regulatory changes before they become mandatory.",
    significant: false,
    image: ""
  }];
  return;
}
type Milestone = {
  year: string;
  title: string;
  description: string;
  significant: boolean;
  image: string;
};
function MilestoneEntry({
  milestone,
  isLeft,
  index,
  ...rest
}: {
  milestone: Milestone;
  isLeft: boolean;
  index: number;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} className={`relative mb-20 md:mb-28 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} {...rest}>
      {}
      <div className="absolute left-1/2 top-6 -translate-x-1/2 flex items-center justify-center z-10">
        {milestone.significant ? <div className="w-3.5 h-3.5 rounded-lg" style={{
        backgroundColor: "var(--primary)"
      }} aria-hidden="true" /> : <div className="w-2 h-2 rounded-lg border" style={{
        backgroundColor: "var(--background)",
        borderColor: "var(--primary)"
      }} aria-hidden="true" />}
      </div>

      {}
      <div className={`hidden md:block absolute top-8 h-px w-12 ${isLeft ? "right-1/2 mr-2" : "left-1/2 ml-2"}`} style={{
      backgroundColor: "var(--primary)"
    }} aria-hidden="true" />

      {}
      <div className={`pl-10 md:pl-0 md:grid md:grid-cols-2 md:gap-16`}>
        {}
        <div className={`${isLeft ? "block md:text-right md:pr-16" : "hidden md:block"}`}>
          {isLeft && <MilestoneContent milestone={milestone} alignRight />}
        </div>

        {}
        <div className={`${!isLeft ? "block md:text-left md:pl-16" : "hidden md:block"}`}>
          {!isLeft && <MilestoneContent milestone={milestone} alignRight={false} />}
        </div>
      </div>
    </div>;
}
function MilestoneContent({
  milestone,
  alignRight
}: {
  milestone: Milestone;
  alignRight: boolean;
}) {
  return <div className={`${alignRight ? "md:flex md:flex-col md:items-end" : ""}`}>
      <div className={`italic font-light leading-none mb-4 ${milestone.significant ? "text-5xl md:text-[56px]" : "text-4xl md:text-5xl"}`} style={{
      color: "var(--primary)",
      fontFamily: "var(--font-serif)"
    }}>
        {milestone.year}
      </div>
      <h3 className={`font-default text-foreground leading-snug mb-3 ${milestone.significant ? "text-[22px] md:text-2xl" : "text-xl md:text-[22px]"}`}>
        {milestone.title}
      </h3>
      <p className={`text-sm leading-relaxed max-w-md ${alignRight ? "md:ml-auto" : ""}`} style={{
      color: "var(--muted-foreground)"
    }}>
        {milestone.description}
      </p>
      {milestone.image && <div className={`mt-6 ${alignRight ? "md:ml-auto" : ""}`}>
          <div className="w-[120px] h-[120px] overflow-hidden border rounded-lg" style={{
        borderColor: "var(--primary)"
      }}>
            <img src={milestone.image} data-wvc-srcset={`${milestone.image}-wvc-srcset`} data-wvc-sizes={`${milestone.image}-wvc-sizes`} alt={milestone.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>}
    </div>;
}